import './InstalledPluginList.css'

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { PluginAdapter } from '@makyoapp/makyo-common'
import { Button, ButtonVariant, SearchBox } from '@makyoapp/makyo-ui'

import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { ORG_ROUTE } from '@/shared/constants'

import PluginDetailModal from './PluginDetailModal'
import { usePluginFilterContext } from './PluginFilterProvider'
import PluginOauth2Modal from './PluginOauth2Modal'

const categories = ['All', 'AI', 'Authentication', 'Database', 'Action', 'OAuth-Database']

function getCategoryBadgeColor(category: string): string {
  return (
    {
      AI: 'bg-purple-100 text-purple-800 border-purple-200',
      Authentication: 'bg-blue-100 text-blue-800 border-blue-200',
      Database: 'bg-green-100 text-green-800 border-green-200',
      Analytics: 'bg-purple-100 text-purple-800 border-purple-200',
      Payment: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      Communication: 'bg-pink-100 text-pink-800 border-pink-200',
      Integration: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      Action: 'bg-orange-100 text-orange-800 border-orange-200',
      'OAuth-Database': 'bg-teal-100 text-teal-800 border-teal-200',
    }[category] || 'bg-gray-100 text-gray-800 border-gray-200'
  )
}

function getPluginCategories(plugin: PluginAdapter): string[] {
  if (plugin.categories && plugin.categories.length > 0) {
    return plugin.categories
  }
  return ['Integration']
}

function renderCategoryBadges(categories: string[]): JSX.Element[] {
  return categories.map((category, index) => (
    <span
      key={`${category}-${index}`}
      className={`text-xs px-2 py-1 rounded border ${getCategoryBadgeColor(category)}`}
    >
      {category}
    </span>
  ))
}

/**
 * Org Plugins Index Page
 *
 * @route `/o/:orgId/plugins`
 */
export default function PluginPage() {
  const { plugins, isLoading } = usePluginFilterContext()
  const { currentOrg } = useCurrentOrgContext()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [showModal, setShowModal] = useState(false)
  const [selectedPlugin, setSelectedPlugin] = useState<PluginAdapter | null>(null)

  const filteredPlugins = useMemo(() => {
    let filtered = plugins

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (plugin) =>
          plugin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          plugin.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter((plugin) => {
        const pluginCategories = getPluginCategories(plugin)
        return pluginCategories.includes(selectedCategory)
      })
    }

    return filtered
  }, [plugins, searchTerm, selectedCategory])

  const handlePluginClick = (plugin: PluginAdapter) => {
    setSelectedPlugin(plugin)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedPlugin(null)
  }

  return (
    <div className='space-y-6'>
      {/* Search and Filter Section */}
      <div className='flex flex-col sm:flex-row gap-4'>
        <div className='relative flex-1'>
          <SearchBox
            value={searchTerm}
            onSearch={setSearchTerm}
            onClearSearch={() => setSearchTerm('')}
            noBorder
            className='border-b w-full bdr bg-white dark:bg-slate-800'
          />
        </div>
        <div className='flex gap-2 flex-wrap'>
          {categories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? ButtonVariant.Normal : ButtonVariant.Outlined}
              onClick={() => setSelectedCategory(category)}
              className={selectedCategory === category ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}
            >
              {category}
            </Button>
          ))}
        </div>
      </div>

      {/* Plugins Grid */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
        {isLoading ? (
          [...Array(8)].map((_, idx) => (
            <div key={idx} className='border bdr rounded-md px-4 py-6'>
              <div className='gap-2.5 flex w-full'>
                <div className='animate-pulse size-10 rounded-xl bg-zinc-300 dark:bg-slate-700' />
                <div className='space-y-2 animate-pulse'>
                  <div className='h-3.5 w-24 rounded-sm bg-zinc-300 dark:bg-slate-700' />
                  <div className='h-4 w-12 rounded-sm bg-zinc-300 dark:bg-slate-700' />
                </div>
                <div className='animate-pulse w-10 h-3 bg-zinc-300 dark:bg-slate-700 ml-auto rounded-sm' />
              </div>

              <div className='mt-4 h-3 w-1/2 rounded-sm animate-pulse bg-zinc-300 dark:bg-slate-700' />

              <div className='mt-4 h-8 w-full rounded-sm animate-pulse bg-zinc-300 dark:bg-slate-700' />
            </div>
          ))
        ) : filteredPlugins.length === 0 ? (
          <div className='py-4'>
            <p className='text-muted-foreground'>No plugins found matching your criteria.</p>
          </div>
        ) : (
          filteredPlugins.map((plugin) => (
            <div
              key={plugin.id}
              className='bg-white text-card-foreground rounded-lg p-4 border border-border h-40 flex flex-col dark:bg-slate-800 dark:border-slate-500'
            >
              <div className='flex items-center mb-3'>
                <div className='w-10 h-10 bg-muted rounded-md overflow-hidden mr-3'>
                  <img src={plugin.icon} alt={`${plugin.name} logo`} className='w-full h-full object-cover' />
                </div>
                <div className='flex-1 min-w-0'>
                  <h4 className='text-sm font-medium text-foreground truncate'>{plugin.name}</h4>
                  <div className='flex items-start justify-between mt-1 gap-2'>
                    <div className='flex flex-wrap gap-1 flex-1'>
                      {renderCategoryBadges(getPluginCategories(plugin))}
                    </div>
                    <span className='text-xs text-muted-foreground flex-shrink-0'>v{plugin.version}</span>
                  </div>
                </div>
              </div>
              <div className='flex-1 flex flex-col justify-end'>
                <p className='text-xs text-muted-foreground mb-3 line-clamp-2'>{plugin.description}</p>
                <button
                  onClick={() => handlePluginClick(plugin)}
                  className='w-full bg-teal-600 hover:bg-teal-700 text-white text-xs py-2 px-3 rounded transition-colors'
                >
                  Add Credentials
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal &&
        selectedPlugin &&
        (selectedPlugin.auth?.type === 'oauth2' ? (
          <PluginOauth2Modal
            plugin={selectedPlugin}
            show={showModal}
            onClose={handleCloseModal}
            onSuccess={() => {
              navigate(`${ORG_ROUTE}/${currentOrg?.id}/plugins/credentials`)
            }}
          />
        ) : (
          <PluginDetailModal plugin={selectedPlugin} show={showModal} onClose={handleCloseModal} />
        ))}
    </div>
  )
}
